'use client'

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { usePathname } from 'next/navigation'

// Types for chat messages
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  toolCalls?: ToolCall[]
  uiComponents?: UIComponentData[]
  isLoading?: boolean
  missingParams?: Record<string, ParamSchema>
}

export interface ParamSchema {
  name: string
  description: string
  type: string
  required: boolean
  enum?: string[]
  default?: string | number | boolean
  placeholder?: string
}

export interface ToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

// Types for policies
export interface AIPolicy {
  id: string
  name: string
  description: string
  naturalLanguage: string
  dsl: PolicyDSL | null
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PolicyDSL {
  conditions: PolicyCondition[]
  actions: PolicyAction[]
  match_mode?: 'all' | 'any'
  stop_on_match?: boolean
}

export interface PolicyCondition {
  field: string
  operator: 'eq' | 'neq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'between' | 'in' | 'not_in' | 'matches' | 'is_null' | 'is_not_null'
  value: string | number | boolean | unknown[] | null
  field_path?: string
}

export interface PolicyAction {
  type: 'auto_approve' | 'auto_reject' | 'flag_review' | 'notify' | 'set_status' | 'set_field' | 'add_note' | 'add_tag' | 'escalate' | 'custom'
  value?: string | number | boolean | Record<string, unknown>
  params?: Record<string, unknown>
}

// Types for insights
export interface AIInsight {
  id: string
  type: 'pattern' | 'anomaly' | 'recommendation'
  severity: 'critical' | 'warning' | 'info'
  title: string
  description: string
  data?: Record<string, unknown>
  suggestedAction?: string
  createdAt: Date
}

// Types for agent responses
export interface UIComponentData {
  type: 'LineChart' | 'BarChart' | 'DataTable' | 'InfoCard' | 'CodeBlock'
  title?: string
  data: Record<string, unknown>
}

export interface IntegrationSource {
  integration_id: string
  system_type: string
  resource_name?: string
  icon_url?: string
}

export interface WidgetConfig {
  xAxisKey?: string
  yAxisKeys?: string[]
  series?: Array<Record<string, unknown>>
}

export interface WidgetPayload {
  should_render: boolean
  type?: 'LineChart' | 'BarChart' | 'DataTable' | 'InfoCard' | 'CodeBlock'
  title?: string
  description?: string
  data?: unknown
  config?: WidgetConfig
}

export interface AgentResponse {
  type: 'completion' | 'clarification_required' | 'approval_required' | 'error'
  content?: string
  ui_components?: UIComponentData[]  // Legacy
  widget?: WidgetPayload  // New: Mission Control widget
  source?: IntegrationSource  // New: Integration attribution
  suggestions?: string[]  // New: Follow-up suggestions
  missing_params?: Record<string, { name: string; description: string; type: string; required: boolean; enum?: string[] }>
  clarification_message?: string
  approval_message?: string
  pending_action?: Record<string, unknown>
  error_message?: string
  tool_calls_executed?: Array<{ id: string; name: string; args: Record<string, unknown>; result?: unknown }>
  follow_up_suggestions?: string[]
}

// Context state type
interface AIContextState {
  // AI Manager state
  isManagerOpen: boolean
  openManager: (contextType?: string, contextId?: string) => void
  closeManager: () => void
  toggleManager: () => void
  
  // Mission Control - Split Screen state
  viewMode: 'collapsed' | 'expanded'
  setViewMode: (mode: 'collapsed' | 'expanded') => void
  rightPaneOpen: boolean
  setRightPaneOpen: (open: boolean) => void
  activeWidget: WidgetPayload | null
  setActiveWidget: (widget: WidgetPayload | null) => void
  
  // Chat state (legacy - for backward compatibility)
  chatHistory: ChatMessage[]
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  clearHistory: () => void
  isTyping: boolean
  setIsTyping: (typing: boolean) => void
  draftMessage: string
  setDraftMessage: (message: string) => void
  
  // Conversation state (new)
  currentConversationId: string | null
  conversationContext: { type?: string; id?: string } | null
  startConversation: (contextType: string, contextId?: string, initialMessage?: string) => Promise<string>
  sendMessage: (message: string) => Promise<{ agentResponse: AgentResponse; conversationId: string } | null>
  provideParams: (params: Record<string, string | number | boolean>) => Promise<void>
  clarifySystem: (system: string) => Promise<AgentResponse | null>
  loadConversation: (conversationId: string) => Promise<void>
  
  // Page context
  currentPageContext: string
  
  // Policies state
  policies: AIPolicy[]
  setPolicies: (policies: AIPolicy[]) => void
  addPolicy: (policy: AIPolicy) => void
  updatePolicy: (id: string, updates: Partial<AIPolicy>) => void
  removePolicy: (id: string) => void
  
  // Insights state
  insights: AIInsight[]
  setInsights: (insights: AIInsight[]) => void
  pendingInsightsCount: number
  markInsightAsRead: (id: string) => void
  
  // Suggestions
  hasPendingSuggestions: boolean
  setHasPendingSuggestions: (has: boolean) => void
  
  // Cisco AI Defense
  aiDefenseEnabled: boolean
  setAiDefenseEnabled: (enabled: boolean) => void
}

const AIContext = createContext<AIContextState | undefined>(undefined)

export function useAI() {
  const context = useContext(AIContext)
  if (!context) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function AIProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  
  // AI Manager state
  const [isManagerOpen, setIsManagerOpen] = useState(false)
  
  // Mission Control - Split Screen state
  const [viewMode, setViewMode] = useState<'collapsed' | 'expanded'>('collapsed')
  const [rightPaneOpen, setRightPaneOpen] = useState(false)
  const [activeWidget, setActiveWidget] = useState<WidgetPayload | null>(null)
  
  // Chat state (legacy)
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const [draftMessage, setDraftMessage] = useState('')
  
  // Conversation state (new)
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null)
  const [conversationContext, setConversationContext] = useState<{ type?: string; id?: string } | null>(null)
  
  // Policies state
  const [policies, setPolicies] = useState<AIPolicy[]>([])
  
  // Insights state
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [readInsightIds, setReadInsightIds] = useState<Set<string>>(new Set())
  
  // Suggestions state
  const [hasPendingSuggestions, setHasPendingSuggestions] = useState(false)
  
  // Cisco AI Defense state
  const [aiDefenseEnabled, setAiDefenseEnabled] = useState(true)
  
  // Derive page context from pathname
  const currentPageContext = pathname || '/'
  
  // Manager controls
  const openManager = useCallback((contextType?: string, contextId?: string) => {
    if (contextType && contextId) {
      setConversationContext({ type: contextType, id: contextId })
    }
    setIsManagerOpen(true)
  }, [])
  
  const closeManager = useCallback(() => {
    setIsManagerOpen(false)
    // Clear draft message when closing
    setDraftMessage('')
    // Don't clear conversation - allow resume
  }, [])
  
  const toggleManager = useCallback(() => setIsManagerOpen(prev => !prev), [])
  
  // Chat controls
  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateId(),
      timestamp: new Date(),
    }
    
    // If this is a real assistant message (not loading), remove any existing loading messages first
    if (message.role === 'assistant' && !message.isLoading) {
      setChatHistory(prev => [...prev.filter(m => !m.isLoading), newMessage])
    } else {
      setChatHistory(prev => [...prev, newMessage])
    }
  }, [])
  
  const clearHistory = useCallback(() => {
    setChatHistory([])
    setCurrentConversationId(null)
    setConversationContext(null)
    setDraftMessage('')
  }, [])
  
  // Conversation methods (new)
  const startConversation = useCallback(async (
    contextType: string,
    contextId?: string,
    initialMessage?: string
  ): Promise<string> => {
    try {
      const { agentApi } = await import('@/lib/agent-api')
      
      const response = await agentApi.startConversation({
        context_type: contextType as 'incident' | 'general' | 'policy',
        context_id: contextId,
        initial_message: initialMessage,
        auto_execute_safe: contextType === 'incident', // Auto-execute for incident investigations
        skip_ai_defense: !aiDefenseEnabled
      })
      
      setCurrentConversationId(response.conversation_id)
      setConversationContext({ type: contextType, id: contextId })
      
      // If initial message was provided, add to chat history
      if (initialMessage && response.agent_response) {
        setChatHistory([
          {
            id: generateId(),
            role: 'user',
            content: initialMessage,
            timestamp: new Date()
          },
          {
            id: generateId(),
            role: 'assistant',
            content: response.agent_response.content || '',
            timestamp: new Date()
          }
        ])
      }
      
      return response.conversation_id
    } catch (error) {
      console.error('Failed to start conversation:', error)
      throw error
    }
  }, [aiDefenseEnabled])
  
  const sendMessage = useCallback(async (message: string): Promise<{ agentResponse: AgentResponse; conversationId: string } | null> => {
    try {
      const { agentApi } = await import('@/lib/agent-api')
      
      // If no conversation yet, merge start + first message in ONE backend call
      // (avoids the extra round-trip of startConversation then sendMessage)
      if (!currentConversationId) {
        const contextType = conversationContext?.type || 'general'
        const contextId = conversationContext?.id
        
        const response = await agentApi.startConversation({
          context_type: contextType as 'incident' | 'general' | 'policy',
          context_id: contextId,
          initial_message: message,   // ← sends message in same call as conversation creation
          auto_execute_safe: contextType === 'incident',
          skip_ai_defense: !aiDefenseEnabled
        })
        
        setCurrentConversationId(response.conversation_id)
        setConversationContext({ type: contextType, id: contextId })
        
        // Return both response and newly created ID
        return {
          agentResponse: (response.agent_response as AgentResponse),
          conversationId: response.conversation_id
        }
      }
      
      // Existing conversation: normal sendMessage call
      const response = await agentApi.sendMessage(currentConversationId, {
        message,
        auto_execute_safe: conversationContext?.type === 'incident',
        skip_ai_defense: !aiDefenseEnabled
      })
      
      return {
        agentResponse: response.agent_response as AgentResponse,
        conversationId: currentConversationId
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      return null
    }
  }, [currentConversationId, conversationContext, aiDefenseEnabled])
  
  const provideParams = useCallback(async (params: Record<string, string | number | boolean>): Promise<void> => {
    try {
      if (!currentConversationId) {
        throw new Error('No active conversation')
      }
      
      const { agentApi } = await import('@/lib/agent-api')
      
      await agentApi.provideParams(currentConversationId, {
        params,
        resume_execution: true
      })
    } catch (error) {
      console.error('Failed to provide params:', error)
      throw error
    }
  }, [currentConversationId])

  const clarifySystem = useCallback(async (system: string): Promise<AgentResponse | null> => {
    try {
      if (!currentConversationId) {
        throw new Error('No active conversation')
      }
      
      const { agentApi } = await import('@/lib/agent-api')
      
      const response = await agentApi.clarify(currentConversationId, {
        system
      })
      
      return response.agent_response
    } catch (error) {
      console.error('Failed to clarify system:', error)
      return null
    }
  }, [currentConversationId])

  const loadConversation = useCallback(async (conversationId: string): Promise<void> => {
    try {
      console.log('[AIContext] Loading conversation:', conversationId)
      const { agentApi } = await import('@/lib/agent-api')
      
      console.log('[AIContext] Fetching conversation from API...')
      const response = await agentApi.getConversation(conversationId)
      const conversation = response.conversation
      console.log('[AIContext] Conversation fetched:', conversation)
      
      // Set the current conversation ID
      setCurrentConversationId(conversationId)
      console.log('[AIContext] Set conversation ID')
      
      // Set the conversation context
      setConversationContext({
        type: conversation.type,
        id: conversation.context_id
      })
      console.log('[AIContext] Set conversation context')
      
      // Load the message history into chat
      const messages: ChatMessage[] = conversation.history.map((msg) => ({
        id: generateId(),
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        toolCalls: msg.tool_calls,
        uiComponents: msg.ui_components
      }))
      
      console.log('[AIContext] Mapped messages:', messages.length)
      setChatHistory(messages)
      console.log('[AIContext] Conversation loaded successfully')
      
    } catch (error) {
      console.error('[AIContext] Failed to load conversation:', error)
      throw error
    }
  }, [])
  
  // Policy controls
  const addPolicy = useCallback((policy: AIPolicy) => {
    setPolicies(prev => [...prev, policy])
  }, [])
  
  const updatePolicy = useCallback((id: string, updates: Partial<AIPolicy>) => {
    setPolicies(prev => 
      prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date() } : p)
    )
  }, [])
  
  const removePolicy = useCallback((id: string) => {
    setPolicies(prev => prev.filter(p => p.id !== id))
  }, [])
  
  // Insights controls
  const pendingInsightsCount = insights.filter(i => !readInsightIds.has(i.id)).length
  
  const markInsightAsRead = useCallback((id: string) => {
    setReadInsightIds(prev => new Set([...prev, id]))
  }, [])
  
  // Keyboard shortcut for AI Manager (Cmd+J or Ctrl+J)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'j' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleManager()
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleManager])
  
  const value: AIContextState = {
    // Manager
    isManagerOpen,
    openManager,
    closeManager,
    toggleManager,
    
    // Mission Control - Split Screen
    viewMode,
    setViewMode,
    rightPaneOpen,
    setRightPaneOpen,
    activeWidget,
    setActiveWidget,
    
    // Chat (legacy)
    chatHistory,
    addMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    draftMessage,
    setDraftMessage,
    
    // Conversation (new)
    currentConversationId,
    conversationContext,
    startConversation,
    sendMessage,
    provideParams,
    clarifySystem,
    loadConversation,
    
    // Page context
    currentPageContext,
    
    // Policies
    policies,
    setPolicies,
    addPolicy,
    updatePolicy,
    removePolicy,
    
    // Insights
    insights,
    setInsights,
    pendingInsightsCount,
    markInsightAsRead,
    
    // Suggestions
    hasPendingSuggestions,
    setHasPendingSuggestions,
    
    // Cisco AI Defense
    aiDefenseEnabled,
    setAiDefenseEnabled,
  }
  
  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  )
}

